import React, { useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import "./Reviews.css";

// ---- Website Reviews ----
const websiteReviews = [
  { name: "Rajubhai", rating: 5, text: "Good cab service" },
  {
    name: "Pooja Trivedi",
    rating: 5,
    text: "The team was very responsive on WhatsApp. They helped me book a last-minute cab without any hassle. Loved the service!",
  },
  {
    name: "Krishna Mehta",
    rating: 5,
    text: "I frequently use their cab for corporate travel. They are always punctual and maintain excellent service quality.",
  },
  {
    name: "Amit Shah",
    rating: 5,
    text: "Clean car, polite driver, and reached exactly on time. Highly recommended for outstation trips.",
  },
  {
    name: "Neha Joshi",
    rating: 5,
    text: "Booked a cab for the airport at 4 AM and everything went smoothly. Great experience overall.",
  },
];

// ---- Google Reviews ----
const googleReviews = [
  {
    name: "Sudhanshu Mishra",
    time: "7 months ago",
    rating: 5,
    text: "Very nice and comfortable service",
    initial: "S",
    color: "#1c2b4a",
  },
  {
    name: "Jayshree Variava",
    time: "7 months ago",
    rating: 5,
    text: "Very good 💯",
    initial: "J",
    color: "#8b2fc9",
  },
  {
    name: "Content Creation",
    time: "7 months ago",
    rating: 5,
    text: "I have a ride of 100 km from vadodra it was smooth he is kind enough",
    initial: "C",
    color: "#0f9b8e",
  },
];

const Stars = ({ count = 5 }) => (
  <div className="stars">
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} size={16} fill="#D4AF37" stroke="#D4AF37" />
    ))}
  </div>
);

const Reviews = () => {
  const [startIndex, setStartIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const visibleCount = 3;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (!isAutoSliding) return;
    
    const interval = setInterval(() => {
      setStartIndex((prev) => (prev + 1) % websiteReviews.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoSliding]);

  const prevSlide = () => {
    setIsAutoSliding(false);
    setStartIndex((prev) =>
      prev === 0 ? websiteReviews.length - 1 : prev - 1
    );
    // Resume auto-sliding after 10 seconds of inactivity
    setTimeout(() => setIsAutoSliding(true), 10000);
  };

  const nextSlide = () => {
    setIsAutoSliding(false);
    setStartIndex((prev) => (prev + 1) % websiteReviews.length);
    // Resume auto-sliding after 10 seconds of inactivity
    setTimeout(() => setIsAutoSliding(true), 10000);
  };

  const visibleReviews = Array.from({ length: visibleCount }).map(
    (_, i) => websiteReviews[(startIndex + i) % websiteReviews.length]
  );

  // Dot indicators
  const totalDots = websiteReviews.length;
  const currentDot = startIndex % totalDots;

  return (
    <>
      {/* ---------- Website Reviews ---------- */}
      <section className="reviews website-reviews">
        <div className="reviews-container">
          {/* Decorative Elements */}
          <div className="reviews-decoration"></div>

          <div className="reviews-header">
            <div className="reviews-eyebrow">
              <span className="reviews-eyebrow-line"></span>
              Testimonials
            </div>
            <h2>What Our <span>Customers Say</span></h2>
            <p>Real reviews from real people who have traveled with us</p>
          </div>

          <div className="carousel">
            {!isMobile && (
              <button className="carousel-arrow left" onClick={prevSlide}>
                <ChevronLeft size={24} />
              </button>
            )}

            <div className="carousel-wrapper">
              <div className="carousel-track">
                {visibleReviews.map((review, index) => (
                  <div className="review-card" key={index}>
                    <Stars count={review.rating} />
                    <p className="review-text">"{review.text}"</p>
                    <h4 className="review-name">- {review.name}</h4>
                  </div>
                ))}
              </div>

              {/* Dot Indicators */}
              <div className="carousel-dots">
                {Array.from({ length: totalDots }).map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${index === currentDot ? 'active' : ''}`}
                    onClick={() => {
                      setIsAutoSliding(false);
                      setStartIndex(index);
                      setTimeout(() => setIsAutoSliding(true), 10000);
                    }}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {!isMobile && (
              <button className="carousel-arrow right" onClick={nextSlide}>
                <ChevronRight size={24} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ---------- Google Reviews ---------- */}
      <section className="reviews google-reviews">
        <div className="reviews-container">
          <div className="reviews-header">
            <div className="reviews-eyebrow">
              <span className="reviews-eyebrow-line"></span>
              Google Reviews
            </div>
            <h2>What <span>Google</span> Says</h2>
            <p>Reviews from our customers on Google</p>
          </div>

          <div className="google-grid">
            <div className="google-summary-card">
              <div className="summary-icon">⭐</div>
              <h3>Bhole Cab Services</h3>
              <div className="summary-rating">
                <span className="rating-number">5.0</span>
                <Stars count={5} />
              </div>
              <p className="read-reviews">Read our 5 Reviews</p>
            </div>

            {googleReviews.map((review, index) => (
              <div className="google-review-card" key={index}>
                <div className="google-review-top">
                  <div
                    className="avatar"
                    style={{ backgroundColor: review.color }}
                  >
                    {review.initial}
                  </div>
                  <div>
                    <h4>{review.name}</h4>
                    <span className="time-ago">{review.time}</span>
                  </div>
                </div>
                <Stars count={review.rating} />
                <p className="google-review-text">"{review.text}"</p>
                <div className="google-review-footer">
                  <a
                    href="https://www.google.com/maps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-on-google"
                  >
                    <span className="google-g">G</span> View on Google
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Reviews;